import { faUser } from "@fortawesome/pro-solid-svg-icons";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Pill } from "@aviary/components/Pill";
import { l } from "@aviary/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome";

import type { AviaryAvatarSize } from "../Avatar.types";
import { PlatformBadge } from "../PlatformBadge";

import * as styles from "./DeprecatedAvatar.styles";

interface AvatarProps {
  /**
   * Profile photo (if uploaded) for the user
   */
  image?: string;
  /**
   * User's first name
   */
  firstName?: string;
  /**
   * User's last name
   */
  lastName?: string;
  /**
   * Specify if the avatar is using the "light" profile, has a white background with dark text. To be used on dark backgrounds only
   *
   * @default false
   */
  isLight?: boolean;
  /**
   * size of the Avatar
   */
  size?: AviaryAvatarSize;
  /**
   * Label, required for aria purposes when an image is present
   */
  label: string;
  /**
   * Specify if the avatar is for a Wellevate user
   * Overlays Wellevate platform badge on the avatar
   * @default false
   */
  isWellevate?: boolean;
  /**
   * Position of the children content overlayed on the Avatar
   */
  overlayPosition?: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" | "center";
  /**
   * Indicates the status of the user, if archived or blocked
   *
   */
  userStatus?: "archived" | "blocked";
  children?: ReactNode;
}

const DeprecatedAvatar = ({
  image,
  firstName,
  lastName,
  label,
  isLight,
  size = "medium",
  isWellevate,
  overlayPosition = "bottomLeft",
  children,
  userStatus,
  ...rest
}: AvatarProps) => {
  const { t } = useTranslation();

  const wrapperStyles = [
    styles.avatar,
    size && styles.sizes[size],
    isLight && styles.isLight,
    userStatus && !image && styles.avatarWithStatus,
  ];

  const overlayChipStyles = [
    styles.overlayChip,
    overlayPosition && styles.overlayPosition[overlayPosition](size, isWellevate),
  ];

  const canRenderName = firstName && lastName && firstName.length > 0 && lastName.length > 0;

  const getNonAvatarContents = () => {
    if (canRenderName) {
      const initials = firstName[0] + lastName[0];

      return initials.toUpperCase();
    }

    return <FontAwesomeIcon icon={faUser} />;
  };

  const renderStatusPill = () => {
    if (!userStatus) return null;

    if (userStatus === "archived") {
      return (
        <div css={styles.userStatusPosition}>
          <Pill isColor="warning" size="xsmall">
            {t(l.aviary.avatar.archived)}
          </Pill>
        </div>
      );
    }

    return (
      <div css={styles.userStatusPosition}>
        <Pill isColor="danger" size="xsmall">
          {t(l.aviary.avatar.blocked)}
        </Pill>
      </div>
    );
  };

  const renderOverlayChip = () => {
    if (isWellevate && !children) {
      return (
        <div css={overlayChipStyles}>
          <PlatformBadge size={size} />
        </div>
      );
    }

    return children && <div css={overlayChipStyles}>{children}</div>;
  };

  const renderImageOrName = () => {
    if (image) {
      return <img src={image} css={styles.image} alt={label} aria-hidden={!label} />;
    }

    return <span>{getNonAvatarContents()}</span>;
  };

  return (
    <div css={wrapperStyles} {...rest}>
      {renderImageOrName()}
      {renderStatusPill()}
      {renderOverlayChip()}
    </div>
  );
};

export { DeprecatedAvatar };
